/**
 * The WistiaFunctionality class
 * This class is responsible for the interaction with the Wistia videos
 * @class WistiaFunctionality
 *
 */

import { decrypt } from "../../utils/functions";
import { requestGraphQL } from "../../utils/requests";
import { services } from "./services";
import { WistiaAmplitude } from "./amplitude";

class WistiaFunctionality {
  // TODO: Antes tiraba de "div[data-wistia-video='true']", si realmente no se usa en ningún otro sitio, podemos limpiar esto de los partials
  init = () => {
    const videos = document.querySelectorAll(".js-wistia-video, .js-video-player-widget");
    // Add Event in play icon for check if user hace a permissions and play the video
    videos.forEach((video) => {
      video.addEventListener("click", (event) => {
        if (event.currentTarget.dataset.checkUrl){
          this.checkVideoAccess(event.currentTarget.dataset.checkUrl);
          delete event.currentTarget.dataset.checkUrl;
        }
      });
    });
  };

  checkVideoAccess = (url) => {
    // Hasta que BE no me devuelva este get con un JSON, tengo que seguir usando ajax, hablar en el SU
    // requestApi(services.getVideo(url));
    // eslint-disable-next-line no-undef
    return $.ajax({
      url,
      type: "GET",
      dataType: "script",
    });
  };

  // If an unit is open, you aren't logged in, clean of cookies, you must leave the modal to register when you play
  initModal = (scope, freePlusUrl) => {
    const videos = document.querySelectorAll(".js-wistia-video");
    // Add Event in play /
    videos.forEach((video) => {
      video.addEventListener("click", () => {
        Domestika.free_plus_modal_auto.open_modal(scope, freePlusUrl);
      });
    });
  };

  // TODO: Los containers llegan en jQuery porque los xhr hibridos entre haml y js legacy están en jQuery, poco a poco refactorizar esto
  playVideo = (videoContainer, encryptedVideoId, audioLanguage = null, captionsLanguage = null) => {
    const time = this.setAutoPlayTime(videoContainer);
    const buttonNext = videoContainer.attr("data-last-video") === "false";
    this.initEmbed(
      videoContainer,
      encryptedVideoId,
      true,
      time,
      true,
      null,
      buttonNext,
      audioLanguage,
      captionsLanguage
    );
    // TODO: Revisar el porqué de las clases estas raras
    videoContainer.unbind("click").addClass("video-player--playing");
    this.pauseOtherVideos();
    this.trackActivity(videoContainer);
    // this.privacyMode();
  };

  setAutoPlayTime = (videoContainer) => {
    const videoContainerHashed = window.location.hash.replace("#", "");
    if (videoContainerHashed === videoContainer.attr("id")) {
      Domestika.url_parser.get_param_value_from_current_url("time");
    }
  };

  // Init embed video after check if user have a permissions
  initEmbed = async (
    container,
    encryptedId,
    autoplay = false,
    time = null,
    embed = true,
    courseLessonId = null,
    buttonNext = true,
    audioLanguage = null,
    captionsLanguage = null
  ) => {
    let wistiaEmbed;
    let videoPlayed = false;
    const courseId = container.attr("data-course-id");
    const decryptedId = decrypt(encryptedId);
    const originalAudio = container.attr("data-audio-original-language");
    let subtitleLanguage = captionsLanguage || container.attr("data-captions-language") || "_off_";
    const audioLabel = audioLanguage !== "off" && audioLanguage ? audioLanguage : originalAudio;
    const options = {
      autoPlay: autoplay,
      silentAutoPlay: false,
      time,
      fullScreen: true,
      googleAnalytics: false,
      playerColor: "505050",
      copyLinkAndThumbnailEnabled: false,
      plugin: {
        "wistia-skip-buttons": {
          src: container.attr("data-skip-buttons-js"),
        },
        "wistia-next-button": {
          hasNextVideoLesson: buttonNext,
          src: container.attr("data-next-button-js"),
        },
        "wistia-pip-button": {
          src: container.attr("data-pip-button-js"),
        },
        "captions-v1": {
          onByDefault: container.attr("data-captions-language") != null,
          language: container.attr("data-captions-language"),
        },
        "wistia-audio-control": {
          src: container.attr("data-audio-control-js"),
        },
      },
      wistiaNextButton: buttonNext,
      wistiaPiPButton: true,
      wistiaAudio: {
        activated: !!container.attr("data-audio-activated"),
        audioLanguage,
        originalAudio,
        locale: container.attr("data-audio-locale"),
        captionsLanguage: subtitleLanguage,
      },
    };

    if (embed) {
      options.container = container.attr("id");
      wistiaEmbed = Wistia.embed(decryptedId, options);
    } else {
      wistiaEmbed = Wistia.api(container.attr("id"));
      wistiaEmbed.replaceWith(decryptedId, options);
      if (buttonNext) {
        wistiaEmbed.container.dataset.courseLessonId = courseLessonId;
      }
    }

    // Get last progress saved
    let progressData = requestGraphQL(
      services.getProgress(wistiaEmbed.container.dataset.videoItemId)
    );
    // return last progress saved
    progressData = await progressData.then((data) => {
      return data?.data?.data?.videoItems?.edges[0]?.node.videoItemUserProgression?.progress || 0;
    });
    const hasEventsActive = window.ENV?.isTrackingProgress();
    if (hasEventsActive) {
      Domestika.wistiaEvents.init(wistiaEmbed);
    }

    const wistiaAmplitude = new WistiaAmplitude({
      amplitudeProps: wistiaEmbed.container.dataset.amplitudeProps,
      secondsPreviouslyWatched: progressData,
      videoDuration: wistiaEmbed.duration(),
    });

    const getCurrentPercentage = ({ percentage = wistiaEmbed.percentWatched() } = {}) => {
      const seenPercentage = (progressData || 0) / Math.floor(wistiaEmbed.duration());
      const currentPercentage = seenPercentage + percentage;
      const roundedPercentage = Math.floor(currentPercentage * 100);
      return {
        currentPercentage,
        roundedPercentage,
      };
    };

    wistiaEmbed.unbind();
    wistiaEmbed.bind("play", () => {
      let shouldTrack = false;
      // Go to last progress saved if progress is less of total duration - 10 seconds
      // Review if hasEventsProgress are active
      if (hasEventsActive) {
        // If no have progress or is 0 track start video
        // If progress is less of total duration - 10 seconds track resume and begin by last progress less 2 seconds
        // If progress is more of total duration - 10 seconds track start-again
        if (!videoPlayed) {
          let isResume = false;
          if (progressData <= wistiaEmbed.duration() - 10) {
            wistiaEmbed.time(progressData - 2);
            isResume = true;
          }

          wistiaAmplitude.trackPlayVideo({ isResume });

          wistiaEmbed.unbind("percentwatchedchanged");
          wistiaEmbed.bind("percentwatchedchanged", (percentage) => {
            if (!wistiaAmplitude.pendingPercentagesToTrack()) {
              wistiaEmbed.unbind("percentwatchedchanged");
              return;
            }

            const { currentPercentage, roundedPercentage } = getCurrentPercentage({ percentage });
            wistiaAmplitude.traceablePercentages[roundedPercentage]?.track({ currentPercentage });
          });
        }
      }

      const idVideoPlayed = container.attr("id");
      const elementVideoPlayer = document.querySelector(`#${idVideoPlayed}`).querySelector("video");
      const elementInPiP = document.pictureInPictureElement;
      if (elementInPiP) {
        if (elementInPiP !== elementVideoPlayer) {
          document.exitPictureInPicture().catch((err) => new Error(err));
          elementVideoPlayer.requestPictureInPicture().catch((err) => new Error(err));
        }
      }

      wistiaEmbed.unbind("captionschange");
      wistiaEmbed.bind("captionschange", (captionsSettings) => {
        subtitleLanguage = captionsSettings.language;
        shouldTrack = true;
        // eslint-disable-next-line no-undef
        return $.ajax({
          type: "PATCH",
          url: "/xhr/users/subtitle_settings/0",
          processData: false,
          data: `visible=${captionsSettings.visible}&language=${subtitleLanguage}`,
        });
      });

      videoPlayed = true;
    });

    wistiaEmbed.bind("end", () => {
      const { roundedPercentage } = getCurrentPercentage();
      wistiaAmplitude.endVideoTraceablePercentages[roundedPercentage]?.track();

      // eslint-disable-next-line prefer-destructuring, no-param-reassign
      courseLessonId = container.attr("id").match(/course_lesson_(.*?)_video/)[1];
      Domestika.video_attachable.add_ended_callback(
        `/xhr/video_items/${container.attr("data-video-item-id")}/ended_play`,
        container
      );
    });
  };

  trackingLabel = (courseId, audioLabel, subtitleLanguage) => {
    return `${courseId}_audio:${audioLabel}_subtitles:${subtitleLanguage}`;
  };

  // Pause other videos when we play one new video
  pauseOtherVideos = () => {
    // eslint-disable-next-line no-underscore-dangle
    window._wq = window._wq || [];
    // eslint-disable-next-line no-undef
    _wq.push({
      id: "_all",
      onReady: (video) => {
        video.bind("play", () => {
          const videos = Wistia.api.all();
          videos.forEach((item) => {
            if (item.hashedId() !== video.hashedId()) {
              item.pause();
            }
          });
        });
      },
    });
  };

  // Pause all video are playing
  pauseAllVideos = () => {
    const videos = Wistia.api.all();
    videos.forEach((item) => {
      item.pause();
    });
  };

  // Track activity
  // TODO: ¿Esto que traquea exactamente? Para ponerle un nombre más específico y no jugar a los adivinos
  trackActivity = (videoContainer) => {
    const activityUrl = videoContainer.data("track-activity");
    if (activityUrl && !videoContainer.hasClass("activity-tracked")) {
      videoContainer.addClass("activity-tracked");
      // eslint-disable-next-line no-undef
      $.ajax({
        url: activityUrl,
        type: "POST",
      });
    }
  };

  // If user go a one videolesson by click, we active scroll for go to this video in units
  autoScrollFromUrl = (hashName) => {
    const hash = Domestika.url_parser.get_hash_from_current_url(hashName);
    // eslint-disable-next-line no-undef
    const videoContainer = $(hash);
    if (videoContainer.length !== 0) {
      Domestika.nav_main.hide();
      Domestika.smooth_scroll.goTo(videoContainer.selector);
      videoContainer.trigger("click");
    }
  };

  getOriginData = () => {
    const playCourseOrigin = localStorage.getItem("playCourse");
    if (playCourseOrigin) {
      const data = JSON.parse(playCourseOrigin);
      localStorage.removeItem("playCourse");
      return data.origin;
    }
    return false;
  };

  // Disable tracking for a single visitor
  privacyMode = () => {
    window._wq = window._wq || [];
    window._wq.push(function (W) {
      W.consent(false);
    });
  };
}

Domestika.wistiaFunctionality = new WistiaFunctionality();
